import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, effect, model, OnDestroy, OnInit, signal, ViewEncapsulation, WritableSignal, input, output, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CollapsibleDirective } from '../../directives/collapsibleDirective.directive';
import { DebounceInputDirective } from '../../directives/debounce-input.directive';
import { ActionsPlacement, FilterInputTypes, IFilterInput } from '../../interfaces/filter-input.interface';
import { Value } from '../../interfaces/value.interface';
import { TooltipOptions } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { CascadeSelectModule } from 'primeng/cascadeselect';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectFilterOptions, SelectModule } from 'primeng/select';
import { DialogService } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, Subscription } from 'rxjs';
import { ButtonComponent } from '../button/button.component';
import { FiltersDialogComponent } from './filters-dialog/filters-dialog.component';
import { FiltersFormComponent } from './filters-form/filters-form.component';

export interface RemoteConfigurations<T = any> {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: { [key: string]: string };
  body: T;
}

@Component({
  selector: 'app-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    InputNumberModule,
    MultiSelectModule,
    CheckboxModule,
    DatePickerModule,
    SelectModule,
    DebounceInputDirective,
    TagModule,
    CollapsibleDirective,
    CascadeSelectModule,
    ButtonComponent,
    TooltipModule,
    FiltersFormComponent,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class FiltersComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpClient);
  private readonly dialogService = inject(DialogService);

  readonly remoteFilters = input<boolean>(false);
  readonly remoteConfigurations = input<RemoteConfigurations>();
  readonly filters = input<IFilterInput[]>([]);
  readonly sectionTitle = input<string>('');
  readonly collapsed = input<boolean>(false);
  readonly filterActionsPlacement = input<ActionsPlacement>('end');
  readonly AllowUpdateFilterControls = input<boolean>(false);
  readonly updatedControls = input<IFilterInput | IFilterInput[]>();
  readonly numberOfResults = model<number>(0);
  readonly filterToggleIcon = input<string>('icon-funnel-simple');

  /**Tag configuration */
  readonly showTitleTag = input<boolean>(false);
  readonly tagSeverity = input<'secondary' | 'success' | 'info' | 'progress' | 'warning' | 'pending' | 'help' | 'danger'>('secondary');
  readonly tagValue = input<string>('secondary');

  /** main buttons text*/
  readonly filterToggleText = input<string>('Filters');
  readonly clearButtonText = input<string>('Clear');
  readonly ApplyButtonText = input<string>('Apply');

  public readonly applyFilters = output<{
    filters: any;
    search: string;
    filterResults: any;
  }>();
  public readonly filtersClear = output<void>();
  public readonly showHideFilters = output<boolean>();
  public readonly searchChange = output<{
    filters: any;
    search: string;
  }>();
  public readonly errorHappen = output<string>();

  public filterInputs: IFilterInput[] = [];
  public filterForm!: FormGroup;
  public showFilter: WritableSignal<boolean> = signal(false);
  public isApplied: WritableSignal<boolean> = signal(false);
  public searchModel: string = '';
  public filterResults: Value[] = [];

  tooltipOptions: TooltipOptions = {
    showDelay: 500,
    tooltipEvent: 'hover',
    tooltipPosition: 'bottom',
  };

  private httpSubscription?: Subscription;

  constructor() {
    effect(() => {
      // React to updatedControls changes when opt-in is enabled
      this.updatedControls();
      if (this.AllowUpdateFilterControls()) {
        this.updateFilterControls();
      }
    });
  }

  ngOnInit() {
    this.showFilter.set(this.collapsed());
    if (this.remoteFilters()) {
      this.httpSubscription = this.makeHttpRequest().subscribe(filters => {
        this.filterInputs = filters;
        this.createForm();
      });
    } else {
      this.filterInputs = this.filters();
      this.createForm();
    }
  }

  ngOnDestroy() {
    this.httpSubscription?.unsubscribe();
  }

  showFilters() {
    this.showFilter.update(x => !x);
    this.showHideFilters.emit(this.showFilter());
  }

  openFiltersDialog() {
    const dialogConfig = {
      showHeader: false,
      width: '640px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '768px': '90vw',
      },
      data: {
        filterForm: this.filterForm,
        filterActionsPlacement: this.filterActionsPlacement(),
        filters: this.filters(),
        clearButtonText: this.clearButtonText(),
        ApplyButtonText: this.ApplyButtonText(),
      },
    };
    const ref = this.dialogService.open(FiltersDialogComponent, dialogConfig)!;

    ref.onClose.subscribe(res => {
      if (res?.action) {
        if (res.action == 'submit') {
          this.submit();
        }
        if (res.action == 'clear') {
          this.clearFilters();
        }
      }
    });
  }

  onSearch() {
    if (this.searchModel == '' || this.searchModel?.length > 1) {
      this.submit();
    }
  }

  submit() {
    if (!this.validateRangeFields()) {
      return;
    }

    this.filterResults = this.prepareFilterResult();
    this.applyFilters.emit({ filters: this.prepareFormBody(), search: this.searchModel, filterResults: this.filterResults });
    this.showFilter.set(false);
    this.showHideFilters.emit(false);
    this.isApplied.set(true);
  }

  clearFilters() {
    this.filterForm.reset();
    this.searchModel = '';
    this.filtersClear.emit();
    this.isApplied.set(false);
    this.filterResults = [];
    this.numberOfResults.set(0);
  }

  clearResults() {
    this.clearFilters();
    this.showFilter.set(true);
    if (!this.showFilter()) {
      this.showHideFilters.emit(true);
    }
  }

  customFilterFunction(event: KeyboardEvent, options: SelectFilterOptions): void {
    if (options.filter) {
      options.filter(event);
    } else {
      console.error('Filter function is undefined.');
    }
  }

  removeSelectedFilter(controlName: string, index: number) {
    this.filterResults = this.filterResults.filter((_, i) => i !== index);

    this.filterForm.controls[controlName].reset();
    if (!this.filterResults.length) {
      this.filterForm.reset();
      this.isApplied.set(false);
      if (!this.showFilter()) {
        this.showFilter.set(true);
        this.showHideFilters.emit(this.showFilter());
      }
    }
    this.applyFilters.emit({ filters: this.prepareFormBody(), search: this.searchModel, filterResults: this.filterResults });
  }

  private validateRangeFields(): boolean {
    for (const filter of this.filterInputs) {
      if (filter.type === FilterInputTypes._timeRange || filter.type === FilterInputTypes._dateRange) {
        const fromControlName = filter.rangeFilterReferences?.[0].controlName;
        const toControlName = filter.rangeFilterReferences?.[1].controlName;
        const fromValue = this.filterForm.get(`${filter.controlName}.${fromControlName}`)?.value;
        const toValue = this.filterForm.get(`${filter.controlName}.${toControlName}`)?.value;

        if (fromValue && toValue && fromValue > toValue) {
          this.errorHappen.emit(`'To' value should be greater than 'From' value for ${filter.resultLabel}`);
          return false;
        }
      }
    }
    return true;
  }

  /**
   *  Handel Time range to Validation if the type is timeRange check if range from and range to is passed
   *
   * @param filter - Input filter from input list
   */

  private validateTimeRange(filter: IFilterInput) {
    if (filter.type === FilterInputTypes._timeRange) {
      if (filter.rangeFilterReferences?.length !== 2 || filter.rangeFilterReferences[0].type !== FilterInputTypes._timeRangeFrom || filter.rangeFilterReferences[1].type !== FilterInputTypes._timeRangeTo) {
        throw new Error(`Invalid rangeFilterReferences for filter type ${filter.type} with controlName ${filter.controlName}`);
      }
    }
  }

  /**
   *  Handel Date range to Validation if the type is dateRange check if range from and range to is passed
   *
   * @param filter - Input filter from input list
   */

  private validateDateRange(filter: IFilterInput) {
    if (filter.type === FilterInputTypes._dateRange) {
      if (filter.rangeFilterReferences?.length !== 2 || filter.rangeFilterReferences[0].type !== FilterInputTypes._dateRangeFrom || filter.rangeFilterReferences[1].type !== FilterInputTypes._dateRangeTo) {
        throw new Error(`Invalid rangeFilterReferences for filter type ${filter.type} with controlName ${filter.controlName}`);
      }
    }
  }

  /**
   *  Handel DateTime to Validation do not pass single time and time range or single or date range just one of the single or range,
   *  also handel the duplicate date && time filter
   *
   * @param filter - Input filter from input list
   */

  private validateSingleTypes(filter: IFilterInput) {
    const rangeTypes = new Set([FilterInputTypes._dateRange, FilterInputTypes._timeRange]);

    const rangeTypeCounts = this.filterInputs.reduce(
      (acc, f) => {
        if (rangeTypes.has(f.type)) {
          acc[f.type] = (acc[f.type] || 0) + 1;
        }
        return acc;
      },
      {} as Record<string, number>,
    );

    if ((filter.type === FilterInputTypes._date && rangeTypeCounts[FilterInputTypes._dateRange] > 0) || (filter.type === FilterInputTypes._time && rangeTypeCounts[FilterInputTypes._timeRange] > 0)) {
      throw new Error(`Cannot use both single and range types for filter with controlName ${filter.controlName}`);
    }
  }

  /**
   *  Handel filter form filter inputs also include:
   *  - validate time range.
   *  - validate date range.
   *  - validate date time single type.
   *  - validate if the type dropdown or multiSelect check if the options and option label available.
   */

  private createForm() {
    let group: any = {};

    this.filterInputs.forEach(filter => {
      this.validateTimeRange(filter);
      this.validateDateRange(filter);
      this.validateSingleTypes(filter);

      const defaultValue = filter.defaultValue ?? null;

      if (filter.type === FilterInputTypes._dropdown || filter.type === FilterInputTypes._multiSelect) {
        if (!filter.options || !filter.optionLabel) {
          throw new Error(`Missing options or optionLabel for filter type ${filter.type} with controlName ${filter.controlName}`);
        }

        // Determine the default value
        const defaultValue = this.determineDefaultValue(filter);

        // Create form control with default value and subscribe to changes
        const control = this.fb.control({
          value: defaultValue,
          disabled: !!filter.disabled,
        });

        control.valueChanges.subscribe(value => {
          if (filter.onChange) {
            filter.onChange(value);
          }
        });

        group[filter.controlName] = control;
      }

      if (filter.type === FilterInputTypes._timeRange || filter.type === FilterInputTypes._dateRange) {
        const subGroup: any = {};
        filter.rangeFilterReferences?.forEach(rangeFilter => {
          const defaultValue = rangeFilter.defaultValue;
          subGroup[rangeFilter.controlName] = this.fb.control({
            value: defaultValue,
            disabled: !!rangeFilter.disabled,
          });
        });

        // Create a FormGroup for the range filter
        group[filter.controlName] = this.fb.group(subGroup);
      } else {
        group[filter.controlName] = {
          value: defaultValue,
          disabled: !!filter.disabled,
        };
      }
    });

    this.filterForm = this.fb.group(group);
  }

  /**
   * Safely extracts label and value from an item, handling potential packaging issues
   * and cases where consuming apps only pass values instead of objects
   */
  private safeExtractItemData(item: any, filter?: IFilterInput): { label: any; value: any } {
    // If item is already a proper object with label and value
    if (typeof item === 'object' && item !== null && (item.label !== undefined || item.value !== undefined)) {
      const label = item?.label ?? item?.['label'];
      const value = item?.value ?? item?.['value'];
      return { label, value };
    }

    // If item is just a primitive value (number, string, etc.)
    // Try to reconstruct the object using the filter's options
    if (filter?.options && (typeof item === 'number' || typeof item === 'string')) {
      const matchingOption = filter.options.find(option => option.value === item);
      if (matchingOption) {
        return { label: matchingOption.label, value: matchingOption.value };
      }
    }

    // Fallback: treat the item as both label and value
    return { label: item, value: item };
  }

  /**
   * Extract the response body from the form builder to use it with apply action or search also with result chips.
   */

  private prepareFormBody(): { [key: string]: any } {
    const body: { [key: string]: any } = {};
    Object.entries(this.filterForm.value).forEach(([key, value]) => {
      const filter = this.filters().find(f => f.controlName === key);

      if (!filter) {
        return;
      }

      if (filter.type === FilterInputTypes._checkbox) {
        body[key] = (value as Array<boolean>)?.length === 1;
      } else if (filter.type === FilterInputTypes._multiSelect) {
        if (Array.isArray(value) && value.length > 0) {
          body[key] = value.map(item => {
            const { label, value: itemValue } = this.safeExtractItemData(item, filter);
            return { label, value: itemValue };
          });
        } else {
          body[key] = [];
        }
      } else if (value && typeof value === 'object' && !Array.isArray(value) && Object.entries(value).length > 1) {
        value = Object.entries(value).reduce(
          (acc, [key, value]) => {
            acc[key] = value ?? '';
            return acc;
          },
          {} as Record<string, string>,
        );
        body[key] = value ?? '';
      } else {
        body[key] = value ?? '';
      }
    });

    return body;
  }

  private fillFilterResult(currentEntry: any, filterResults: Value[], currentFilter: IFilterInput) {
    const isWhitespaceString = (str: string) => str.replaceAll(/\s/g, '').length;

    if (!currentEntry) {
      return;
    }

    const [, value] = currentEntry;
    const resultItem = this.createResultItem(value, currentFilter, isWhitespaceString);

    if (resultItem) {
      filterResults.push(resultItem);
      this.handleTimeFormatting(filterResults, currentFilter);
    }
  }

  private createResultItem(value: any, currentFilter: IFilterInput, isWhitespaceString: (str: string) => number): Value | null {
    if (typeof value === 'boolean' && value) {
      return this.createBooleanResult(currentFilter);
    }

    if (Array.isArray(value) && value.length) {
      return this.createArrayResult(value, currentFilter);
    }

    if (typeof value === 'object' && value?.label) {
      return this.createObjectResult(value, currentFilter);
    }

    if (this.isValidStringOrNumber(value, isWhitespaceString)) {
      return this.createStringOrNumberResult(value, currentFilter);
    }

    return null;
  }

  private createBooleanResult(currentFilter: IFilterInput): Value {
    return {
      label: currentFilter.resultLabel,
      value: true,
      moreInformation: {
        controlName: currentFilter.controlName,
      },
    };
  }

  private createArrayResult(value: any[], currentFilter: IFilterInput): Value {
    // This is just for display in the filter chips
    const { label: firstItemLabel } = this.safeExtractItemData(value[0], currentFilter);
    const resultArrayString = `${firstItemLabel ?? 'Unknown'}${value.length > 1 ? '...+' + (value.length - 1) : ''}`;

    return {
      label: currentFilter.resultLabel,
      value: value
        .map(v => {
          const { value: extractedValue } = this.safeExtractItemData(v, currentFilter);
          return extractedValue;
        })
        .filter(val => val !== null && val !== undefined),
      valueLabel: resultArrayString,
      moreInformation: {
        controlName: currentFilter.controlName,
      },
    };
  }

  private createObjectResult(value: any, currentFilter: IFilterInput): Value {
    return {
      label: currentFilter.resultLabel,
      value: value.value,
      valueLabel: value.label,
      moreInformation: {
        controlName: currentFilter.controlName,
      },
    };
  }

  private createStringOrNumberResult(value: string | number, currentFilter: IFilterInput): Value {
    return {
      label: currentFilter.resultLabel,
      value: value,
      moreInformation: {
        controlName: currentFilter.controlName,
      },
    };
  }

  private isValidStringOrNumber(value: any, isWhitespaceString: (str: string) => number): boolean {
    return !!(typeof value === 'string' && isWhitespaceString(value)) || typeof value === 'number';
  }

  private handleTimeFormatting(filterResults: Value[], currentFilter: IFilterInput): void {
    const isTimeLabel = currentFilter.resultLabel.toLowerCase() == 'from time' || currentFilter.resultLabel.toLowerCase() == 'to time';

    if (isTimeLabel) {
      const resultObj = filterResults.find(x => x.label == currentFilter.resultLabel);
      if (resultObj) {
        resultObj.valueLabel = this.formatTimeHHmmToHmma(resultObj.value);
      }
    }
  }

  /**
   * Converts a time string from HH:mm (24-hour) format to h:mm AM/PM format.
   * Returns the original string if input is invalid.
   * @param timeString - The time string in HH:mm format (e.g., "14:45", "09:30")
   * @returns Formatted time string (e.g., "2:45 PM", "9:30 AM") or original input.
   */
  private formatTimeHHmmToHmma(timeString: string): string {
    if (!timeString || typeof timeString !== 'string') {
      return timeString; // Return original if input is not a valid string
    }

    const timeParts = timeString.split(':');
    if (timeParts.length !== 2) {
      return timeString; // Invalid format
    }

    const hoursStr = timeParts[0];
    const minutesStr = timeParts[1]; // Keep minutes as string (e.g., "05", "30")

    const hours = Number.parseInt(hoursStr, 10);
    const minutes = Number.parseInt(minutesStr, 10); // Parse for validation

    // Validate parsing results
    if (Number.isNaN(hours) || Number.isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || minutesStr.length !== 2) {
      return timeString; // Invalid hours or minutes
    }

    const ampm = hours >= 12 ? 'PM' : 'AM';
    let hours12 = hours % 12;
    hours12 = hours12 ?? 12; // Convert hour '0' to '12'

    return `${hours12}:${minutesStr} ${ampm}`;
  }

  public determineDefaultValue(filter: any): any {
    switch (filter.type) {
      case FilterInputTypes._cascadeSelect:
        return this.findCascadeSelectDefaultValue(filter);

      case FilterInputTypes._dropdown:
        return filter.options.find((item: { value: any }) => item.value === filter.defaultValue) ?? null;

      case FilterInputTypes._multiSelect:
        return filter.options.filter((item: { value: any }) => Array.isArray(filter.defaultValue) && filter.defaultValue.includes(item.value));

      default:
        return null;
    }
  }
  public findCascadeSelectDefaultValue(filter: any): any {
    for (const option of filter.options) {
      const foundItem = option.items.find((item: { value: any }) => item.value === filter.defaultValue);
      if (foundItem) {
        return foundItem;
      }
    }
    return null;
  }

  private prepareFilterResult() {
    const _filterResults: Value[] = [];
    const entries = Object.entries(this.prepareFormBody()).map(([key, value]) => [key, value]);

    this.filters().forEach(filter => {
      let currentEntry = entries.find(([key]) => key === filter.controlName);

      if (!currentEntry) return;

      switch (filter.type) {
        case FilterInputTypes._timeRange:
          if (filter.rangeFilterReferences?.length) this.handleTimeRange(filter, currentEntry);
          break;
        case FilterInputTypes._dateRange:
          if (filter.rangeFilterReferences?.length) this.handleDateRange(filter, currentEntry);
          break;
        case FilterInputTypes._time:
          this.handleTime(currentEntry);
          break;
        case FilterInputTypes._date:
          this.handleDate(currentEntry);
          break;
      }

      this.fillFilterResult(currentEntry, _filterResults, filter);
    });

    return _filterResults;
  }

  private formatTime(date: Date): string {
    return `${date.getHours()}:${date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes()}`;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private handleTimeRange(filter: any, currentEntry: any) {
    const fromControlName = filter.rangeFilterReferences[0].controlName;
    const toControlName = filter.rangeFilterReferences[1].controlName;
    const fromValue = currentEntry[1][fromControlName];
    const toValue = currentEntry[1][toControlName];

    let timeFrom;
    let timeTo;

    if (typeof fromValue === 'object') {
      timeFrom = new Date(fromValue);
    }

    if (typeof toValue === 'object') {
      timeTo = new Date(toValue);
    }

    currentEntry[1] = `${timeFrom ? this.formatTime(timeFrom) : ''} ${timeTo ? 'to ' + this.formatTime(timeTo) : ''}`;
  }

  private handleDateRange(filter: any, currentEntry: any) {
    const fromControlName = filter.rangeFilterReferences[0].controlName;
    const toControlName = filter.rangeFilterReferences[1].controlName;
    const fromValue = currentEntry[1][fromControlName];
    const toValue = currentEntry[1][toControlName];
    if (typeof fromValue === 'string' && !fromValue.length && typeof toValue === 'string' && !toValue.length) {
      return;
    }
    let dateFrom;
    let dateTo;
    if (typeof fromValue === 'object') {
      dateFrom = new Date(fromValue);
    }

    if (typeof toValue === 'object') {
      dateTo = new Date(toValue);
    }

    currentEntry[1] = `${dateFrom ? this.formatDate(dateFrom) : ''} ${dateTo ? 'to ' + this.formatDate(dateTo) : ''}`;
  }

  private handleTime(currentEntry: any) {
    if (typeof currentEntry[1] === 'string' && !currentEntry[1].length) {
      return;
    }
    if (typeof currentEntry[1] === 'object') {
      const time = new Date(currentEntry[1]);
      currentEntry[1] = this.formatTime(time);
    }
  }

  private handleDate(currentEntry: any) {
    if (typeof currentEntry[1] === 'string' && !currentEntry[1].length) {
      return;
    }
    if (typeof currentEntry[1] === 'object') {
      const date = new Date(currentEntry[1]);
      currentEntry[1] = this.formatDate(date);
    }
  }

  private makeHttpRequest(): Observable<IFilterInput[]> {
    const remoteConfigurations = this.remoteConfigurations();
    if (!remoteConfigurations) {
      throw new Error("Missing remote configurations please check it or make 'remoteFilters' false");
    }

    const { url, method, headers, body } = remoteConfigurations;
    const httpOptions = {
      headers: new HttpHeaders(headers),
    };

    switch (method) {
      case 'GET':
        return this.http.get<IFilterInput[]>(url, httpOptions);
      case 'POST':
        return this.http.post<IFilterInput[]>(url, body, httpOptions);
      case 'PUT':
        return this.http.put<IFilterInput[]>(url, body, httpOptions);
      case 'DELETE':
        return this.http.delete<IFilterInput[]>(url, httpOptions);
      default:
        throw new Error('Unsupported HTTP method');
    }
  }

  private updateFilterControls() {
    const updatedControls = this.updatedControls();
    if (updatedControls) {
      if (!Array.isArray(updatedControls) && (updatedControls.type === FilterInputTypes._multiSelect || updatedControls.type === FilterInputTypes._dropdown)) {
        this.filterInputs.forEach(input => {
          if (!Array.isArray(updatedControls) && input.controlName === updatedControls.controlName) {
            input.options = updatedControls.options;
            this.filterForm.controls[input.controlName].reset();
          }
        });
      } else if (!Array.isArray(updatedControls) && !(updatedControls.type === FilterInputTypes._multiSelect || updatedControls.type === FilterInputTypes._dropdown)) {
        throw new Error(`Cannot update the filter with controlName ${updatedControls.controlName},just you can update the dropdowns and multiSelect type`);
      } else if (Array.isArray(updatedControls) && updatedControls.every(input => input.type === FilterInputTypes._multiSelect || input.type === FilterInputTypes._dropdown)) {
        updatedControls.forEach(input => {
          if (this.filterInputs.some(x => x.controlName === input.controlName)) {
            const inputIndex = this.filterInputs.findIndex(x => x.controlName === input.controlName);
            if (inputIndex !== -1) {
              this.filterInputs[inputIndex].options = input.options;
              this.filterForm.controls[input.controlName].reset();
            }
          }
        });
      } else if (Array.isArray(updatedControls) && !updatedControls.every(input => input.type === FilterInputTypes._multiSelect || input.type === FilterInputTypes._dropdown)) {
        let notMatchControls = '';
        updatedControls.forEach((input, i) => {
          if (!(input.type === FilterInputTypes._multiSelect || input.type === FilterInputTypes._dropdown)) {
            notMatchControls += i === 0 ? input.type : ', ' + input.type;
            throw new Error(`Cannot update the filter with controlName ${notMatchControls},just you can update the dropdowns and multiSelect type`);
          }
        });
      }
    }
  }
}
